// ============================================================================
// FILE:    src/app/services/permission.service.ts
// VERSION: v2.1.0 — Race Condition Fix: ensureLoaded() + Diagnose-Logging
// DATE:    2026-05-21
//
// ÄNDERUNGEN v2.1.0 (gegenüber v2.0.0):
//   + ensureLoaded(): garantiert einen geladenen Snapshot, bevor synchron
//     geprüft wird. Schließt die Race Condition beim Page-Refresh /
//     Direkteinstieg auf geschützte Routes (siehe ausführliche Begründung
//     direkt an der Methode).
//   + Diagnose-Logs in refresh() und ensureLoaded(): zeigen Walkthrough-
//     Schritt, erwarteten und Ist-Wert in der Browser-Console.
//
//   NICHT geändert: buildSnapshot(), alle bestehenden Methoden, Datenfluss.
//   isSuperAdmin bleibt orgPermission >= 7.
//
// ----------------------------------------------------------------------------
// ZWECK (Bestand):
//   Liest den Redis-Session-Key (via CalStatusService) und stellt den
//   Permission-Snapshot strukturiert zur Verfügung.
//   Ist die GRUNDLAGE für CapabilityService.
//
// ÄNDERUNGEN v2.0.0 gegenüber v1.x (Bestand):
//
//   1) PermissionSnapshot um zwei neue Maps erweitert:
//      - projectRoles:  Map<pslot, role_key>   ← NEU: explizite Projekt-Rolle
//      - projectStates: Map<pslot, access_state> ← NEU: approved/pending/rejected etc.
//
//      CapabilityService nutzt projectRoles als PRIMÄREN Pfad für Capability-
//      Berechnung. projectPerms bleibt als Legacy-Fallback erhalten.
//
//   2) buildSnapshot() liest primär aus slots{} (neue Redis-Struktur von
//      reg.route.js v2.0.0+) und fällt auf activeProjects[] zurück (Legacy).
//
//   3) isSuperAdmin: orgPermission >= 7 (unverändert).
//      PMO_OFFICER (orgPerm >= 3) hat KEINEN isSuperAdmin-Bypass —
//      er bekommt seine Capabilities über ORG_ROLE_CAPABILITIES.
//
// DATENFLUSS:
//   Redis auth:<uuid>
//     → CalStatusService.getRedisMetaCached()
//     → PermissionService.buildSnapshot()
//     → CapabilityService.hasCapability()
//     → HasCapabilityDirective (*hasCapability="'SPRINT_CREATE'")
//     → projectAccessGuard
//
// CACHE-VERHALTEN:
//   CalStatusService hat 5-Min-Cache. Kein zusätzlicher API-Call solange
//   Cache frisch ist. Bei Permission-Änderung via Admin: nächster Navigation-
//   Event löst Guard-Check aus → CalStatusService.invalidateCache() → Reload.
//
// BACKWARD COMPATIBILITY:
//   projectPerms bleibt vorhanden — alter Code der .get(pslot) nutzt
//   funktioniert weiter. Nur CapabilityService nutzt jetzt primär projectRoles.
// ============================================================================

import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { CalStatusService, RedisMetaResponse } from './cal-status.service';

/**
 * Strukturierter Snapshot der aktuellen Permissions.
 *
 * projectPerms:  Map<pslot, perm>       — Legacy, Fallback
 * projectRoles:  Map<pslot, role_key>   — NEU v2.0.0, primärer Pfad
 * projectStates: Map<pslot, access_state> — NEU v2.0.0, für Guard-Check
 * isSuperAdmin:  true wenn orgPerm >= 7 — Bypass alle Guards
 */
export interface PermissionSnapshot {
  orgPermission:  number;
  projectPerms:   Map<string, number>;   // pslot → perm (Legacy-Fallback)
  projectRoles:   Map<string, string>;   // pslot → role_key (primär ab v2.0.0)
  projectStates:  Map<string, string>;   // pslot → access_state
  isSuperAdmin:   boolean;
  takenAt:        number;
}

@Injectable({ providedIn: 'root' })
export class PermissionService {

  public snapshot$: Observable<PermissionSnapshot | null>;
  private internalSubject = new BehaviorSubject<PermissionSnapshot | null>(null);

  // ── NEU v2.1.0: Single-Flight-Lock für ensureLoaded() ──────────────────────
  // Verhindert, dass mehrere parallele Guard-Calls je einen eigenen refresh()
  // auslösen. Läuft bereits ein refresh(), warten alle auf dieselbe Promise.
  private inflightRefresh: Promise<PermissionSnapshot | null> | null = null;

  constructor(private calStatus: CalStatusService) {
    this.snapshot$ = this.internalSubject.asObservable().pipe(
      distinctUntilChanged((a, b) =>
        a?.takenAt === b?.takenAt && a?.orgPermission === b?.orgPermission
      ),
      shareReplay(1)
    );

    // Initial load — fail silent wenn User noch nicht eingeloggt
    this.refresh().catch(err =>
      console.warn('[PermissionService] Initial refresh failed:', err?.message || err)
    );
  }

  /**
   * Erzwingt Reload der Permission-Daten.
   * Nutzt den 5-Min-Cache von CalStatusService.
   */
  async refresh(): Promise<PermissionSnapshot | null> {
    const meta = await firstValueFrom(this.calStatus.getRedisMetaCached());
    if (!meta) {
      console.warn('[PermissionService] refresh(): meta is null → no snapshot built (user not logged in?)');
      return null;
    }
    const snapshot = this.buildSnapshot(meta);
    this.internalSubject.next(snapshot);
    return snapshot;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // NEU v2.1.0: ensureLoaded() — Race Condition Fix
  // ──────────────────────────────────────────────────────────────────────────
  /**
   * Garantiert, dass ein Snapshot vorhanden ist, BEVOR synchron geprüft wird.
   *
   * HINTERGRUND — die Race Condition, die wir hier schließen:
   *   getSnapshot() liest synchron internalSubject.value. Der wird aber erst
   *   vom async refresh() im Konstruktor befüllt. Bei einem Page-Refresh oder
   *   Direkteinstieg (Bookmark, geteilter Link) läuft der projectAccessGuard,
   *   BEVOR refresh() fertig ist:
   *
   *     1. Angular instanziiert Services frisch → Konstruktor feuert refresh()
   *        (async, ein API-Call zu /api/my_project_slots_from_redis)
   *     2. projectAccessGuard läuft → hasCapability() → getSnapshot()
   *     3. refresh() noch nicht durch → getSnapshot() === null
   *     4. hasCapability trifft `if (!snap) return false` → DENIED
   *        BEVOR der isSuperAdmin-Check überhaupt erreicht wird.
   *
   *   Symptom (beobachtet 2026-05-20/21): Super-Admin (orgPerm=7) landet beim
   *   Direkteinstieg auf /insufficient-permission mit "YOUR CURRENT ROLE: No Access".
   *   Über project-select funktioniert es, weil der Snapshot dann schon warm ist.
   *
   *   ensureLoaded() schließt die Lücke: liefert den vorhandenen Snapshot sofort
   *   zurück (Normalfall, kein API-Call), oder wartet auf refresh() falls noch
   *   keiner da ist. Mehrere parallele Guard-Calls teilen sich denselben refresh()
   *   (Single-Flight via inflightRefresh) statt je einen eigenen auszulösen.
   *
   * VERWENDUNG: projectAccessGuard ruft `await permissions.ensureLoaded()`
   *   VOR dem synchronen hasAllCapabilities()-Check.
   */
  async ensureLoaded(): Promise<PermissionSnapshot | null> {
    const existing = this.getSnapshot();
    if (existing) {
      // Normalfall (Navigation via project-select etc.): Snapshot warm, kein API-Call.
      console.debug(
        '[PermissionService] ensureLoaded(): snapshot warm ' +
        `(orgPerm=${existing.orgPermission}, superAdmin=${existing.isSuperAdmin}) → no reload`
      );
      return existing;
    }

    // Kalter Snapshot (Refresh/Direkteinstieg): auf refresh() warten.
    // Single-Flight: läuft schon einer, hängen wir uns dran statt neu zu starten.
    console.warn(
      '[PermissionService] ensureLoaded(): snapshot is null → awaiting refresh() ' +
      '(this is the Race-Condition path — expected on hard reload / direct link)'
    );

    if (!this.inflightRefresh) {
      this.inflightRefresh = this.refresh().finally(() => {
        this.inflightRefresh = null;
      });
    }

    const loaded = await this.inflightRefresh;
    console.debug(
      '[PermissionService] ensureLoaded(): refresh() done → ' +
      (loaded
        ? `orgPerm=${loaded.orgPermission}, superAdmin=${loaded.isSuperAdmin}`
        : 'still null (not logged in?)')
    );
    return loaded;
  }

  getSnapshot(): PermissionSnapshot | null {
    return this.internalSubject.value;
  }

  // ── Convenience: Projekt-Permission ───────────────────────────────────────

  async getProjectPermission(pslot: string): Promise<number> {
    let snap = this.getSnapshot();
    if (!snap) snap = await this.refresh();
    return snap?.projectPerms.get(pslot) ?? 0;
  }

  getProjectPermissionSync(pslot: string): number {
    return this.getSnapshot()?.projectPerms.get(pslot) ?? 0;
  }

  async hasProjectPermission(pslot: string, min: number): Promise<boolean> {
    let snap = this.getSnapshot();
    if (!snap) snap = await this.refresh();
    if (!snap) return false;
    if (snap.isSuperAdmin) return true;
    return (snap.projectPerms.get(pslot) ?? 0) >= min;
  }

  hasProjectPermissionSync(pslot: string, min: number): boolean {
    const snap = this.getSnapshot();
    if (!snap) return false;
    if (snap.isSuperAdmin) return true;
    return (snap.projectPerms.get(pslot) ?? 0) >= min;
  }

  // ── NEU v2.0.0: Projekt-Rolle via role_key ────────────────────────────────

  /**
   * Gibt den role_key für einen pslot zurück (z.B. 'project_admin').
   * Null wenn kein role_key bekannt → CapabilityService fällt auf perm-Mapping zurück.
   */
  getProjectRoleKey(pslot: string): string | null {
    return this.getSnapshot()?.projectRoles.get(pslot) ?? null;
  }

  /**
   * Gibt den access_state für einen pslot zurück (z.B. 'approved', 'pending').
   * Null wenn kein Eintrag bekannt.
   */
  getProjectAccessState(pslot: string): string | null {
    return this.getSnapshot()?.projectStates.get(pslot) ?? null;
  }

  /**
   * True wenn access_state === 'approved' für diesen pslot.
   * Super-Admin bypassed diese Prüfung.
   */
  isProjectAccessApproved(pslot: string): boolean {
    const snap = this.getSnapshot();
    if (!snap) return false;
    if (snap.isSuperAdmin) return true;
    return snap.projectStates.get(pslot) === 'approved';
  }

  // ── Convenience: Org-Permission ───────────────────────────────────────────

  async getOrgPermission(): Promise<number> {
    let snap = this.getSnapshot();
    if (!snap) snap = await this.refresh();
    return snap?.orgPermission ?? 0;
  }

  getOrgPermissionSync(): number {
    return this.getSnapshot()?.orgPermission ?? 0;
  }

  async hasOrgPermission(min: number): Promise<boolean> {
    return (await this.getOrgPermission()) >= min;
  }

  hasOrgPermissionSync(min: number): boolean {
    return this.getOrgPermissionSync() >= min;
  }

  isSuperAdmin(): boolean {
    return this.getSnapshot()?.isSuperAdmin ?? false;
  }

  // ── Internal: Snapshot aus RedisMetaResponse bauen ────────────────────────

  /**
   * Baut den PermissionSnapshot aus der Redis-Meta-Response.
   *
   * PRIORITÄT der Datenquellen:
   *   1. slots{} (neue Struktur, reg.route.js v2.0.0+) — hat role_key + access_state
   *   2. activeProjects[] (Legacy-Format) — hat nur perm, kein role_key
   *   3. idleAccessRequests[] — für Pending-Logic (perm=0 Einträge)
   *
   * slots{} enthält ab reg.route.js v2.0.0:
   *   { perm, proname, proj_id, role_key, access_state, access_state_date,
   *     requested_at, prev_role_key, prev_access_state, decision_comment }
   */
  private buildSnapshot(meta: RedisMetaResponse): PermissionSnapshot {
    const projectPerms  = new Map<string, number>();
    const projectRoles  = new Map<string, string>();
    const projectStates = new Map<string, string>();

    const anyMeta = meta as any;

    // ── Primär: slots{} (reg.route.js v2.0.0+) ────────────────────────────
    // Enthält role_key und access_state — das ist der vollständige Datensatz.
    if (anyMeta.slots && typeof anyMeta.slots === 'object') {
      for (const [pslot, slot] of Object.entries(anyMeta.slots)) {
        const s = slot as any;
        const perm = Number(s?.perm ?? s?.permission ?? 0);

        projectPerms.set(pslot, perm);

        // role_key: 'project_admin' | 'project_it_pm' | 'project_member' | etc.
        if (s?.role_key) {
          projectRoles.set(pslot, s.role_key);
        }

        // access_state: 'approved' | 'pending' | 'rejected' | 'revoked' | 'suspended'
        // Default 'approved' wenn nicht gesetzt (Legacy-Einträge ohne access_state)
        projectStates.set(pslot, s?.access_state ?? 'approved');
      }
    }

    // ── Fallback: activeProjects[] (Legacy-Format ohne role_key) ──────────
    // Wird genutzt wenn slots{} noch nicht befüllt ist (ältere reg.route.js Version).
    // Setzt KEINE projectRoles → CapabilityService fällt auf perm-Mapping zurück.
    if (Array.isArray(meta.activeProjects)) {
      for (const p of meta.activeProjects) {
        if (p?.pslot && !projectPerms.has(p.pslot)) {
          projectPerms.set(p.pslot, Number(p.permission) || 0);
          // Kein role_key vorhanden → projectRoles bleibt leer für diesen pslot
          // → CapabilityService.computeProjectCapabilities nutzt perm-Fallback
          projectStates.set(p.pslot, 'approved'); // activeProjects = immer approved
        }
      }
    }

    // ── Idle Access Requests (perm=0 Einträge) ────────────────────────────
    // Wichtig: idleAccessRequests sind Einträge die noch nicht approved sind.
    // access_state kommt aus slots{} wenn vorhanden, sonst 'pending' als Default.
    if (Array.isArray(meta.idleAccessRequests)) {
      for (const p of meta.idleAccessRequests) {
        if (p?.pslot && !projectPerms.has(p.pslot)) {
          projectPerms.set(p.pslot, 0);
          // access_state aus slots{} bevorzugt; Fallback 'pending'
          if (!projectStates.has(p.pslot)) {
            projectStates.set(p.pslot, 'pending');
          }
        }
      }
    }

    const orgPermission = Number(meta.orgAccessPermission) || 0;

    return {
      orgPermission,
      projectPerms,
      projectRoles,
      projectStates,
      isSuperAdmin: orgPermission >= 7,   // Super Admin bypassed alle Guards
      takenAt: Date.now()
    };
  }
}
